/**
 * Live AI-scan progress driven by SCAN_STARTED / SCAN_PROGRESS / SCAN_COMPLETED
 * effects. The copilot does not emit these yet (reserved for the pacs-be
 * scan-progress bridge), but the FE handles them so the ScanProgressStrip
 * lights up the moment they ship.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/state/store';

export type ScanStatus = 'running' | 'complete' | 'failed';

export interface CopilotScan {
  scanId: string;
  studyUuid: string;
  product: string;
  status: ScanStatus;
  progress: number;
  startedAt: number;
  completedAt?: number;
  reportId?: string;
}

// Alias used by the ScanProgressStrip view.
export type ScanState = CopilotScan;

interface CopilotScansState {
  byId: Record<string, CopilotScan>;
}

const initialState: CopilotScansState = { byId: {} };

const copilotScansSlice = createSlice({
  name: 'copilotScans',
  initialState,
  reducers: {
    scanStarted: (
      state,
      action: PayloadAction<{
        scanId: string;
        studyUuid: string;
        product: string;
      }>
    ) => {
      const { scanId, studyUuid, product } = action.payload;
      state.byId[scanId] = {
        scanId,
        studyUuid,
        product,
        status: 'running',
        progress: 0,
        startedAt: Date.now(),
      };
    },
    scanProgress: (
      state,
      action: PayloadAction<{
        scanId: string;
        progress: number;
        status?: string;
      }>
    ) => {
      const scan = state.byId[action.payload.scanId];
      if (scan) scan.progress = action.payload.progress;
    },
    scanCompleted: (
      state,
      action: PayloadAction<{ scanId: string; ok: boolean; reportId?: string }>
    ) => {
      const scan = state.byId[action.payload.scanId];
      if (!scan) return;
      scan.status = action.payload.ok ? 'complete' : 'failed';
      scan.progress = 100;
      scan.completedAt = Date.now();
      scan.reportId = action.payload.reportId;
    },
    dismissScan: (state, action: PayloadAction<{ scanId: string }>) => {
      delete state.byId[action.payload.scanId];
    },
  },
});

export const { scanStarted, scanProgress, scanCompleted, dismissScan } =
  copilotScansSlice.actions;
export default copilotScansSlice.reducer;

export const selectAllScans = (state: RootState): CopilotScan[] =>
  Object.values(state.copilotScans.byId);

export const selectScansForStudy =
  (studyUuid: string) =>
  (state: RootState): CopilotScan[] =>
    Object.values(state.copilotScans.byId).filter(
      (s) => s.studyUuid === studyUuid
    );
